import { IonAlert } from '@ionic/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { t } from 'ttag';

type Props = {
  header: string;
  subHeader?: string;
  when: boolean;
  onOk?: () => void;
};

const RouterPrompt = ({ header, subHeader, when, onOk }: Props) => {
  const [path, setPath] = useState('');
  const [showPrompt, setShowPrompt] = useState(false);
  const [trigger, setTrigger] = useState(false);
  const history = useHistory();
  const ref = useRef<null | (() => void)>(null);

  const unblock = () => {
    if (ref.current) {
      ref.current();
      ref.current = null;
    }
  };

  useEffect(() => {
    setTrigger(when);
    if (!when) {
      unblock();
    }
  }, [when]);

  const handleOk = useCallback(() => {
    unblock();
    setPath('');
    setShowPrompt(false);
    if (onOk) {
      onOk();
    }
    setTrigger(false);
    history.replace(path);
  }, [path, history, onOk]);

  const handleCancel = useCallback(() => {
    setShowPrompt(false);
    setPath('');
  }, []);

  useEffect(() => {
    if (trigger) {
      // The types for history@4 are outdated/broken, so we have to use `any` here until we can
      // update to a newer version that has proper types.
      // skipcq: JS-0323
      ref.current = history.block((prompt: any) => {
        setPath(prompt.location.pathname);
        setShowPrompt(true);
        return false;
      });
    }
  }, [history, trigger]);

  return (
    <IonAlert
      isOpen={showPrompt}
      header={header}
      subHeader={subHeader}
      buttons={[
        {
          text: t`Cancel`,
          role: 'cancel',
          handler: handleCancel,
        },
        {
          text: t`OK`,
          role: 'confirm',
          handler: handleOk,
        },
      ]}
    />
  );
};

export default RouterPrompt;
